import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, Observable } from 'rxjs';
import { BulkCreateFinancialYearJobStatusDto } from '../../../../aumico-lib-annualreport-fe/src/lib/dtos/bulk-create-financial-year-job-status.dto';
import { AumicoCoreProvider } from '../../../../aumico-lib-core-fe/src/lib';
import {
    PersonFinancialYearPreviewDto,
    PersonProjectDto,
    PersonProjectsFilter,
} from '../dtos/person-project-dto.interface';
import { ProjectStatus } from '../enums/project-status.enum';
import { RoutesUtil } from '../util/RoutesUtil';

/** REV-22155 — HTTP to `/person-projects`. Task, comment and document actions keep using `/tasks/…`. */
@Injectable({
    providedIn: 'root',
})
export class PersonProjectsService {
    constructor(
        private readonly http: HttpClient,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly routesUtil: RoutesUtil,
    ) {}

    getPersonProjects(
        filter?: PersonProjectsFilter,
    ): Observable<{ personProjects: PersonProjectDto[]; totalCount: number }> {
        return this.blocking(
            'getPersonProjects',
            this.http.get<{ personProjects: PersonProjectDto[]; totalCount: number }>(this.routesUtil.personProjects(), {
                params: this.routesUtil.buildHttpParams(filter),
            }),
        );
    }

    getPersonProject(projectId: string): Observable<PersonProjectDto> {
        return this.blocking('getPersonProject', this.http.get<PersonProjectDto>(this.routesUtil.personProject(projectId)));
    }

    /** Name and assignees come from the person in the backend. */
    createPersonProject(personId: string, year: number): Observable<PersonProjectDto> {
        return this.blocking(
            'createPersonProject',
            this.http.post<PersonProjectDto>(this.routesUtil.personProjects(), { personId, year }),
        );
    }

    updatePersonProjectStatus(
        projectId: string,
        projectStatus: ProjectStatus,
        updateTasks: boolean,
        mandateManagementTaskIds: string[] = [],
    ): Observable<PersonProjectDto> {
        return this.blocking(
            'updatePersonProjectStatus',
            this.http.put<PersonProjectDto>(this.routesUtil.personProject(projectId), {
                projectStatus,
                updateTasks,
                mandateManagementTaskIds,
            }),
        );
    }

    bulkUpdatePersonProjectStatus(
        projectIds: string[],
        projectStatus: ProjectStatus,
        updateTasks: boolean,
        mandateManagementTaskIds: string[] = [],
    ): Observable<PersonProjectDto[]> {
        return this.blocking(
            'bulkUpdatePersonProjectStatus',
            this.http.put<PersonProjectDto[]>(this.routesUtil.personProject('bulk-update-status'), {
                projectIds,
                projectStatus,
                updateTasks,
                mandateManagementTaskIds,
            }),
        );
    }

    deletePersonProject(projectId: string): Observable<void> {
        return this.blocking('deletePersonProject', this.http.delete<void>(this.routesUtil.personProject(projectId)));
    }

    bulkDeletePersonProjects(projectIds: string[]): Observable<{ deletedIds: string[] }> {
        return this.blocking(
            'bulkDeletePersonProjects',
            this.http.delete<{ deletedIds: string[] }>(this.routesUtil.personProject('bulk-delete'), {
                body: { projectIds },
            }),
        );
    }

    /** "Synchronisieren" with the person template; `sync: false` only loads tasks, comments, documents. */
    syncPersonProject<Response>(projectId: string, sync: boolean): Observable<Response> {
        return this.blocking(
            'syncPersonProject',
            this.http.put<Response>(this.routesUtil.personProject(projectId, '/sync'), { sync }),
        );
    }

    getPersonProjectTasks<Response>(projectId: string, filter?: object): Observable<Response> {
        return this.http.get<Response>(this.routesUtil.personProject(projectId, '/tasks'), {
            params: this.routesUtil.buildHttpParams(filter),
        });
    }

    /** A task added by hand. */
    createPersonProjectTask<Task>(projectId: string, task: object): Observable<Task> {
        return this.blocking(
            'createPersonProjectTask',
            this.http.post<Task>(this.routesUtil.personProject(projectId, '/tasks'), task),
        );
    }

    exportPersonProjectDocuments(projectId: string, inventoryData: unknown): Observable<Blob> {
        return this.blocking(
            'exportPersonProjectDocuments',
            this.http.post(
                this.routesUtil.personProject(projectId, '/documents/export'),
                { inventoryData },
                { responseType: 'blob' },
            ),
        );
    }

    previewNextFinancialYear(personIds: string[]): Observable<PersonFinancialYearPreviewDto> {
        return this.blocking(
            'previewNextFinancialYear',
            this.http.post<PersonFinancialYearPreviewDto>(
                this.routesUtil.personProject('bulk-create-financial-year', '/preview'),
                { personIds },
            ),
        );
    }

    startNextFinancialYear(personIds: string[]): Observable<{ jobId: string; status: string; message: string }> {
        return this.http.post<{ jobId: string; status: string; message: string }>(
            this.routesUtil.personProject('bulk-create-financial-year'),
            { personIds },
        );
    }

    /** Job results reuse the company fields: `companyId` = personId, `companyName` = personName. */
    getNextFinancialYearJobStatus(jobId: string): Observable<BulkCreateFinancialYearJobStatusDto> {
        return this.http.get<BulkCreateFinancialYearJobStatusDto>(
            this.routesUtil.personProject('bulk-create-financial-year', `/status/${jobId}`),
        );
    }

    private blocking<T>(name: string, request$: Observable<T>): Observable<T> {
        const key = `[Aumico-Person-Projects] ${name}`;
        this.coreProvider.dispatchBlockingRequest(key);
        return request$.pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd(key)));
    }
}
